import { useState } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField
} from '@mui/material';

interface ChatInputProps {
  onSend: (content: string) => void;
  isSending: boolean;
  disabled?: boolean;
  onAttach?: () => void;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isSending,
  disabled = false,
  onAttach,
  placeholder = 'Write a message...'
}) => {
  const [value, setValue] = useState('');

  const handleSend = () => {
    if (isSending || disabled) {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    onSend(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
      <TextField
        multiline
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'center', m: 0 }}>
              <Stack direction="row" gap={1} alignItems="center">
                {onAttach && (
                  <IconButton
                    size="small"
                    onClick={onAttach}
                    disabled={disabled || isSending}
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      bgcolor: 'info.main'
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                )}

                <Button
                  loading={isSending}
                  disabled={!value.trim() || isSending || disabled}
                  onClick={handleSend}
                  variant="contained"
                  color="primary"
                  sx={{
                    minWidth: 90,
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'text.disabled'
                    }
                  }}
                >
                  Send
                </Button>
              </Stack>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 92,
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: '4px',
            '& fieldset': {
              borderColor: 'divider'
            },
            '&:hover fieldset': {
              borderColor: 'divider'
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main'
            }
          },
          '& .MuiOutlinedInput-inputMultiline': {
            padding: 0,
            fontSize: 14,
            lineHeight: '20px',
            color: 'text.primary'
          }
        }}
      />
    </Box>
  );
};
